import React, { useEffect, useState, useCallback, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Accordion,
  Badge,
  Checkbox,
  ConfirmationModal,
  Feedback,
  Flex,
  Modal,
  Spinner,
  Typography,
} from '@percent/lemonade'
import {
  type AssignedDomain,
  type AssignedDomainRole,
} from '../../types/permissions.types'
import type { EffectiveRoleSource } from '../../types/effectiveRoles.types'
import { RoleSources } from '../roleSources'
import { tenantDomains } from '../../constants/permissions'
import { inheritedLabel, isInherited } from '../../utils/domainAssignment'
import styles from './DomainAccordion.module.scss'
import type { Role } from '../../types/accounts.types'

export type DomainStatus = {
  message?: string
  type: 'loading' | 'success' | 'error'
}

export type DomainAccordionProps = {
  domain: AssignedDomain
  isDisabled?: boolean
  assignAccountRole: (roleId: string) => Promise<AssignedDomain>
  unassignAccountRole: (roleId: string) => Promise<AssignedDomain>
  renderSelectedRoles?: (roles: AssignedDomainRole[]) => React.ReactNode
  // Why each role is held, by role id. Shown beside a role held other than by
  // direct assignment, which the checkbox alone cannot say.
  roleSources?: Record<string, EffectiveRoleSource[]>
  getGroupHref?: (groupId: string) => string | undefined
}

export function DomainStatusView({ status }: { status: DomainStatus }) {
  return (
    <div className={styles.statusView}>
      <Feedback
        variant={
          status.type === 'success'
            ? 'positive'
            : status.type === 'error'
            ? 'critical'
            : 'default'
        }
        icon={
          status.type === 'success'
            ? 'check'
            : status.type === 'error'
            ? 'close'
            : undefined
        }
      >
        <span
          data-testid="domain-status"
          data-status={status.type}
          className={styles.statusMessage}
        >
          {status.type === 'loading' && <Spinner size="s" />}
          <span>{status.message}</span>
        </span>
      </Feedback>
    </div>
  )
}

export function DomainAccordion({
  domain,
  isDisabled = false,
  assignAccountRole,
  unassignAccountRole,
  renderSelectedRoles,
  roleSources,
  getGroupHref,
}: DomainAccordionProps) {
  const { t } = useTranslation()
  const tRef = useRef(t)
  tRef.current = t

  const [status, setStatus] = useState<DomainStatus | undefined>(undefined)
  const [localDomain, setLocalDomain] = useState<AssignedDomain>(domain)
  const [timerRef, setTimerRef] = useState<NodeJS.Timeout | undefined>(
    undefined
  )
  const [confirmationRole, setConfirmationRole] = useState<
    { role: Role; isSelected: boolean } | undefined
  >(undefined)

  const clearStatus = useCallback(() => {
    if (timerRef) clearTimeout(timerRef)
    setTimerRef(
      setTimeout(() => {
        setStatus(undefined)
      }, 3000)
    )
  }, [timerRef])

  // if the domain gets updated outside of this component, clear the status
  useEffect(() => {
    setStatus(undefined)
    setLocalDomain(domain)
  }, [domain, setStatus, setLocalDomain])

  const selectedRoles = localDomain.roles.filter(
    (role) => role.assigned === 'set'
  )

  const requiresConfirmation = tenantDomains.includes(domain.id)

  const handleRoleChange = useCallback(
    async (roleId: string, isSelected: boolean) => {
      setStatus({
        type: 'loading',
        message: tRef.current('drawer.accounts.savingChanges'),
      })

      try {
        const updated = isSelected
          ? await assignAccountRole(roleId)
          : await unassignAccountRole(roleId)

        setLocalDomain(updated)
        setStatus({
          type: 'success',
          message: tRef.current('drawer.accounts.changesSaved'),
        })
      } catch (err) {
        console.error('Failed to update permission:', err)

        setStatus({
          type: 'error',
          message: tRef.current('drawer.accounts.changesCouldNotBeSaved'),
        })
      }
      if (confirmationRole) setConfirmationRole(undefined)
      clearStatus()
    },
    [assignAccountRole, unassignAccountRole, clearStatus, confirmationRole]
  )

  const roleCheckbox = (role: AssignedDomainRole) => {
    return (
      <Checkbox
        key={role.id}
        name={role.id}
        variant="default"
        active={!!role.assigned}
        disabled={
          status?.type === 'loading' ||
          isDisabled ||
          role.assigned === 'inferred'
        }
        onChange={(e) => {
          if (requiresConfirmation) {
            return setConfirmationRole({
              role,
              isSelected: role.assigned !== 'set',
            })
          }
          return handleRoleChange(role.id, role.assigned !== 'set')
        }}
        label={role.name}
        helperText={role.description}
        dataTestId={`checkbox-${role.name}`}
        className={styles.accordionCheckbox}
      />
    )
  }

  const defaultRenderSelectedRoles = (roles: AssignedDomainRole[]) =>
    roles.map((role) => (
      <Badge variant="default" key={role.id}>
        {role.name}
      </Badge>
    ))

  return (
    <Accordion.Item
      key={domain.id}
      value={domain.id}
      className={styles.accordionItem}
      data-testid={`accordion-item-${domain.id}`}
    >
      <Accordion.Control className={styles.accordionControl}>
        <Flex align="baseline" gap={4} className={styles.controlHeader}>
          <Typography variant="default-medium" className={styles.categoryName}>
            {domain.name}
          </Typography>
          {(renderSelectedRoles ?? defaultRenderSelectedRoles)(selectedRoles)}
          {isInherited(localDomain) && (
            <Badge
              variant="default"
              shape="oval"
              size="small"
              testId={`domain-${domain.id}-inherited`}
            >
              {inheritedLabel(t)}
            </Badge>
          )}
        </Flex>
      </Accordion.Control>
      <Accordion.Panel data-testid={`accordion-item-${domain.id}-roles`}>
        <div className={styles.panelContent}>
          {status && <DomainStatusView status={status} />}
          {localDomain.roles.map((role) => (
            <div key={role.id} className={styles.checkbox}>
              {roleCheckbox(role)}
              {role.assigned === 'inferred' &&
                !!roleSources?.[role.id]?.length && (
                  <RoleSources
                    sources={roleSources[role.id]}
                    getGroupHref={getGroupHref}
                    className={styles.roleSources}
                    testId={`role-source-${domain.id}-${role.id}`}
                  />
                )}
            </div>
          ))}
          {confirmationRole && requiresConfirmation && (
            <Modal
              open={true}
              onClose={() => setConfirmationRole(undefined)}
              data-testid="confirmation-modal"
            >
              <ConfirmationModal
                title={t('drawer.accounts.confirmRoleChange')}
                primaryButtonText={t(
                  'drawer.accounts.confirmRoleChangeButton',
                  {
                    set: confirmationRole.isSelected ? 'Set' : 'Unset',
                  }
                )}
                secondaryButtonText={t(
                  'drawer.accounts.confirmRoleChangeCancel'
                )}
                handleSubmit={async () =>
                  handleRoleChange(
                    confirmationRole.role.id,
                    confirmationRole.isSelected
                  )
                }
                handleClose={() => setConfirmationRole(undefined)}
              >
                <Typography>
                  {t('drawer.accounts.confirmRoleChangeDescription', {
                    set: confirmationRole.isSelected ? 'set' : 'unset',
                    role: confirmationRole.role.name,
                  })}
                </Typography>
              </ConfirmationModal>
            </Modal>
          )}
        </div>
      </Accordion.Panel>
    </Accordion.Item>
  )
}
